import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Eye, ChevronDown, ChevronUp, X, Plus } from "lucide-react";
import { Room } from "@/types/checklist";
import { cn } from "@/lib/utils";

interface ChecklistLocalControlsProps {
  onSave: () => void;
  onPreview: () => void;
  onCancel: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  isSaving: boolean;
  isTranslating: boolean;
  allExpanded: boolean;
  /** Checklist name shown in the bar (draft title while editing). */
  editingChecklistName: string;
  /** When true, show a subtle unsaved indicator next to the title. */
  hasUnsavedChanges?: boolean;
  /** Relative time since last successful save (clean state only). */
  savedHint?: string | null;
  /** When true, show the Tasks-only row (rooms, expand/collapse). */
  showTasksToolbar: boolean;
  rooms: Room[];
  activeRoomIndex: number | null;
  onRoomSelect: (index: number) => void;
  onAddRoom: () => void;
}

const ChecklistLocalControls = ({
  onSave,
  onPreview,
  onCancel,
  onExpandAll,
  onCollapseAll,
  isSaving,
  isTranslating,
  allExpanded,
  editingChecklistName,
  hasUnsavedChanges = false,
  savedHint = null,
  showTasksToolbar,
  rooms,
  activeRoomIndex,
  onRoomSelect,
  onAddRoom,
}: ChecklistLocalControlsProps) => {
  const isDisabled = isSaving || isTranslating;

  return (
    <div
      className={cn(
        "sticky top-[60px] z-40 -mx-4 px-4 sm:-mx-6 sm:px-6",
        "rounded-xl border border-border/80 bg-card/95 shadow-sm backdrop-blur-sm",
        "mb-1 overflow-hidden"
      )}
    >
      {/* Row 1: title + document actions */}
      <div className="hidden md:flex flex-col gap-0">
        <div className="flex items-center justify-between gap-4 py-3 px-1 sm:px-2 min-h-[3.25rem]">
          <div className="min-w-0 flex-1 flex items-baseline gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground shrink-0">
              Editing
            </span>
            <span className="text-sm font-semibold text-foreground truncate" title={editingChecklistName}>
              {editingChecklistName || "Untitled checklist"}
            </span>
            {hasUnsavedChanges && (
              <span
                className="inline-flex h-2 w-2 shrink-0 rounded-full bg-amber-500"
                title="Unsaved changes"
                aria-label="Unsaved changes"
              />
            )}
            {savedHint ? (
              <span className="text-xs text-muted-foreground shrink-0 hidden lg:inline" title={`Saved ${savedHint}`}>
                Saved {savedHint}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={onSave} disabled={isDisabled} className="shadow-sm">
              <Save className="w-4 h-4 mr-2" />
              {isTranslating ? "Translating..." : isSaving ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={onPreview}>
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel} className="text-muted-foreground">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>

        {showTasksToolbar && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border/60 py-2.5 px-1 sm:px-2 bg-muted/25">
            <span className="text-xs font-medium text-muted-foreground mr-1 shrink-0">Tasks · Rooms</span>
            {rooms.length > 0 ? (
              <Select
                value={activeRoomIndex !== null ? String(activeRoomIndex) : undefined}
                onValueChange={(value) => onRoomSelect(parseInt(value, 10))}
              >
                <SelectTrigger className="h-8 w-[min(100%,220px)] max-w-[240px] text-sm bg-background">
                  <SelectValue placeholder="Jump to room" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map((room, index) => (
                    <SelectItem key={index} value={String(index)}>
                      {room.name.trim() || `Room ${index + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Button size="sm" variant="outline" onClick={onAddRoom} className="bg-background">
              <Plus className="w-4 h-4 mr-1" />
              Add room
            </Button>
            {rooms.length > 0 && (
              <>
                <div className="hidden sm:block w-px h-6 bg-border mx-0.5" aria-hidden />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={allExpanded ? onCollapseAll : onExpandAll}
                  className="text-muted-foreground"
                >
                  {allExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Collapse all
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Expand all
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Mobile */}
      <div className="md:hidden py-3 space-y-3 px-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Editing</p>
            <p className="text-sm font-semibold leading-snug truncate" title={editingChecklistName}>
              {editingChecklistName || "Untitled checklist"}
              {hasUnsavedChanges ? (
                <span className="inline-block ml-1.5 h-2 w-2 rounded-full bg-amber-500 align-middle" aria-label="Unsaved" />
              ) : null}
            </p>
            {savedHint ? (
              <p className="text-[11px] text-muted-foreground mt-0.5">Saved {savedHint}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" variant="outline" onClick={onPreview} aria-label="Preview checklist">
              <Eye className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel} className="text-muted-foreground px-2" aria-label="Cancel editing">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {showTasksToolbar && (
          <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground px-0.5">Tasks · Rooms</p>
            <div className="flex items-center gap-2">
              {rooms.length > 0 ? (
                <Select
                  value={activeRoomIndex !== null ? String(activeRoomIndex) : undefined}
                  onValueChange={(value) => onRoomSelect(parseInt(value, 10))}
                >
                  <SelectTrigger className="flex-1 h-9 text-sm bg-background">
                    <SelectValue placeholder="Jump to room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((room, index) => (
                      <SelectItem key={index} value={String(index)}>
                        {room.name.trim() || `Room ${index + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <Button size="sm" variant="outline" onClick={onAddRoom} className="shrink-0 bg-background">
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
            {rooms.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="w-full h-9 text-muted-foreground"
                onClick={allExpanded ? onCollapseAll : onExpandAll}
              >
                {allExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Collapse all rooms
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Expand all rooms
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistLocalControls;
